// components/dashboard/RecentActivity.jsx
import React from 'react';
import { Card, Timeline, Tag, Typography, Empty, Skeleton } from 'antd';
import {
  ShoppingCartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/tr';

dayjs.extend(relativeTime);
dayjs.locale('tr');

const { Text } = Typography;

const RecentActivity = ({ orderData, loading = false }) => {
  // Loading skeleton
  if (loading) {
    return (
      <Card
        title={<Skeleton.Input active size="small" style={{ width: 220 }} />}
        style={{ height: '100%', borderRadius: 8 }}
      >
        <div style={{ paddingLeft: 24 }}>
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 24,
                gap: 12
              }}
            >
              <Skeleton.Avatar active size={12} shape="circle" />
              <Skeleton.Input active size="small" style={{ width: 200 }} />
              <Skeleton.Button active size="small" shape="round" style={{ width: 60 }} />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  const getActivityItems = () => {
    if (!orderData) return [];

    const items = [];

    // Bugunku siparisler
    if (orderData.todayOrders > 0) {
      items.push({
        color: 'blue',
        dot: <ShoppingCartOutlined style={{ fontSize: 16 }} />,
        label: dayjs().format('HH:mm'),
        children: (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
          >
            <Text strong style={{ color: '#1890ff', fontSize: 16 }}>{orderData.todayOrders}</Text>
            <Text>yeni siparis bugun</Text>
            <Tag color="blue" style={{ borderRadius: 10, marginLeft: 4 }}>Bugun</Tag>
          </motion.div>
        )
      });
    }

    // Aktif siparisler
    if (orderData.activeOrders > 0) {
      items.push({
        color: 'orange',
        dot: <SyncOutlined spin style={{ fontSize: 16 }} />,
        children: (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
          >
            <Text strong style={{ color: '#faad14', fontSize: 16 }}>{orderData.activeOrders}</Text>
            <Text>aktif siparis devam ediyor</Text>
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <Tag color="orange" style={{ borderRadius: 10 }}>Aktif</Tag>
            </motion.div>
          </motion.div>
        )
      });
    }

    // Bu ayin siparisleri
    if (orderData.thisMonthOrders > 0) {
      items.push({
        color: 'cyan',
        dot: <ClockCircleOutlined style={{ fontSize: 16 }} />,
        children: (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
          >
            <Text strong style={{ color: '#13c2c2', fontSize: 16 }}>{orderData.thisMonthOrders}</Text>
            <Text>siparis bu ay</Text>
            <Tag color="cyan" style={{ borderRadius: 10 }}>{dayjs().format('MMMM')}</Tag>
          </motion.div>
        )
      });
    }

    // Tamamlanan siparisler
    if (orderData.completedOrders > 0) {
      items.push({
        color: 'green',
        dot: <CheckCircleOutlined style={{ fontSize: 16 }} />,
        children: (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.25 }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
          >
            <Text strong style={{ color: '#52c41a', fontSize: 16 }}>{orderData.completedOrders}</Text>
            <Text>siparis tamamlandi</Text>
            <Tag color="success" style={{ borderRadius: 10 }}>Tamamlandi</Tag>
          </motion.div>
        )
      });
    }

    // Iptal edilen siparisler
    if (orderData.cancelledOrders > 0) {
      items.push({
        color: 'red',
        dot: <CloseCircleOutlined style={{ fontSize: 16 }} />,
        children: (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
          >
            <Text strong style={{ color: '#ff4d4f', fontSize: 16 }}>{orderData.cancelledOrders}</Text>
            <Text>siparis iptal edildi</Text>
            <Tag color="error" style={{ borderRadius: 10 }}>Iptal</Tag>
          </motion.div>
        )
      });
    }

    // Basarisiz siparisler
    if (orderData.failedOrders > 0) {
      items.push({
        color: 'grey',
        children: (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.35 }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
          >
            <Text strong style={{ color: '#8c8c8c', fontSize: 16 }}>{orderData.failedOrders}</Text>
            <Text>siparis basarisiz</Text>
            <Tag style={{ borderRadius: 10 }}>Basarisiz</Tag>
          </motion.div>
        )
      });
    }

    return items;
  };

  const activities = getActivityItems();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
    >
      <Card
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <motion.span
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <ClockCircleOutlined style={{ color: '#1890ff' }} />
            </motion.span>
            Son Aktiviteler ve Siparis Ozeti
          </span>
        }
        style={{ height: '100%', borderRadius: 8 }}
        className="card-hover-lift"
        extra={
          activities.length > 0 && (
            <Tag color="processing" style={{ borderRadius: 10 }}>
              {activities.length} aktivite
            </Tag>
          )
        }
      >
        {activities.length > 0 ? (
          <div className="timeline-animated">
            <Timeline
              items={activities}
              style={{ marginTop: 8 }}
            />
          </div>
        ) : (
          <Empty
            description={
              <span style={{ color: '#8c8c8c' }}>
                Son aktivite bulunamadi
              </span>
            }
            style={{ padding: '24px 0' }}
          />
        )}
      </Card>
    </motion.div>
  );
};

export default RecentActivity;
